import axiosInstance from '../../api/axios.js';
import { normalizeRiskRows } from '../../components/risks-table/utils.js';
import { compact, isEqual, uniq } from 'lodash';
import { buildChangedPatch as buildPatchByKeys } from '../../utils/patchUtils.js';
import { getSoxControlsListPage } from './risks.api.js';

export function getRiskLevelFromScore(risk) {
  if (!risk) return null;

  const explicitLevel = String(risk.risk_level ?? risk.risk_category ?? '').trim().toLowerCase();
  if (explicitLevel === 'high' || explicitLevel === 'moderate' || explicitLevel === 'low') {
    return explicitLevel;
  }

  const numericScore = Number.parseFloat(risk.risk_score ?? risk.corporate_risk_score ?? NaN);
  if (Number.isFinite(numericScore)) {
    if (numericScore <= 3.3) return 'low';
    if (numericScore <= 6.6) return 'moderate';
    return 'high';
  }

  return null;
}

/**
 * Stable composite key for a risk row: `processId:nodeId:riskId`.
 * Falls back to `__row:<index>` when the row has no identifying fields.
 */
export function compositeRiskKey(risk) {
  const p = risk.processId ?? risk.process_id;
  const n = risk.nodeId ?? risk.node_id;
  const id = risk.id ?? risk.risk_id ?? risk.riskId;
  const sp = p != null && p !== '' ? String(p) : '';
  const sn = n != null && n !== '' ? String(n) : '';
  const sid = id != null && id !== '' ? String(id) : '';
  if (sp !== '' || sn !== '' || sid !== '') {
    return `${sp}:${sn}:${sid}`;
  }
  return `__row:${risk.__pagedSourceIndex ?? 0}`;
}

/**
 * GET `/api/risks` paged list. Backend owns pagination, search, filtering and sorting; we
 * forward the UI state as query params and render `{ items, pagination }` as-is. Mirrors the
 * Test Runs page pattern (see `fetchTestExecutionsList`).
 */
export async function fetchRisksTablePage(listParams) {
  const {
    page,
    pageSize,
    search,
    filterLevel,
    filterCategory,
    filterControlsMapped,
    filterProcessMapped,
    filterProcess,
    filterCycle,
    filterSource,
    sortKey,
    sortDir,
    auditYearId,
  } = listParams;

  const queryParams = {
    page,
    page_size: pageSize,
    ...(search?.trim() ? { search: search.trim() } : {}),
    ...(sortKey ? { sort_by: sortKey, sort_order: sortDir } : {}),
    ...(filterLevel !== 'all' ? { risk_level: filterLevel } : {}),
    ...(filterCategory !== 'all' ? { risk_category: filterCategory } : {}),
    ...(filterControlsMapped === 'true' || filterControlsMapped === 'false'
      ? { control_mapped: filterControlsMapped === 'true' }
      : {}),
    ...(filterProcessMapped === 'true' || filterProcessMapped === 'false'
      ? { process_mapped: filterProcessMapped === 'true' }
      : {}),
    ...(filterProcess !== 'all' ? { process_id: filterProcess } : {}),
    ...(filterCycle !== 'all' ? { sox_cycle_id: filterCycle } : {}),
    ...(filterSource !== 'all' ? { source: filterSource } : {}),
    ...(auditYearId ? { audit_year_id: auditYearId } : {}),
  };

  const res = await axiosInstance.get('/api/risks', { params: queryParams });
  const body = res.data;

  const items = Array.isArray(body?.items) ? body.items : [];
  const pagination = body?.pagination ?? {};
  const totalItems = typeof pagination.total_items === 'number' ? pagination.total_items : items.length;
  const totalPages = Math.max(1, typeof pagination.total_pages === 'number' ? pagination.total_pages : 1);

  return {
    rows: normalizeRiskRows(items),
    total: totalItems,
    totalPages,
  };
}

export async function fetchRiskProcessFilterOptionsPage({
  page,
  pageSize = 20,
  search = '',
  riskId = '',
  soxCycleCode = '',
}) {
  const requestedPageSize = Math.max(1, Number(pageSize) || 20);
  const requestedPage = Math.max(1, Number(page) || 1);
  const trimmedRiskId = String(riskId ?? '').trim();
  const trimmedCycleCode = String(soxCycleCode ?? '').trim();

  // Cycle-scoped list uses GET /sox-cycles/:cycleId/processes (business cycle_id).
  // Otherwise GET /api/processes (org-wide, optional risk_id).
  const response = await axiosInstance.get(
    trimmedCycleCode
      ? `/api/sox-cycles/${encodeURIComponent(trimmedCycleCode)}/processes`
      : '/api/processes',
    {
      params: {
        page: requestedPage,
        page_size: requestedPageSize,
        ...(search?.trim() ? { search: search.trim() } : {}),
        ...(!trimmedCycleCode && trimmedRiskId ? { risk_id: trimmedRiskId } : {}),
      },
    }
  );

  const body = response.data ?? {};
  const root = body?.data && typeof body.data === 'object' ? body.data : body;

  const list = Array.isArray(root?.processes)
    ? root.processes
    : Array.isArray(root?.items)
      ? root.items
      : Array.isArray(root)
        ? root
        : [];

  const mapped = list
    .map((process) => {
      const value = String(process?.id ?? process?.process_id ?? '').trim();
      if (!value) return null;
      const cycleId = String(process?.cycle_id ?? trimmedCycleCode ?? '').trim();
      const routeProcessId = String(process?.process_id ?? '').trim();
      const processName = String(process?.name ?? process?.process_name ?? routeProcessId).trim();
      const cycleName = String(process?.sox_cycle_name ?? process?.cycle_name ?? '').trim();
      return {
        value,
        label: cycleName
          ? `${cycleId} - ${routeProcessId} - ${processName} (${cycleName})`
          : `${cycleId} - ${routeProcessId} - ${processName}`,
        cycleId,
        routeProcessId,
      };
    })
    .filter(Boolean);

  /** `/api/processes`: `{ processes, pagination: { page, page_size, total_items, total_pages } }` */
  const pag = root?.pagination ?? {};
  const resolvedPage = Number.isFinite(Number(pag.page)) ? Number(pag.page) : requestedPage;
  const totalItems = Number.isFinite(Number(pag.total_items)) ? Number(pag.total_items) : null;
  let totalPages = Number.isFinite(Number(pag.total_pages)) ? Math.max(1, Number(pag.total_pages)) : null;
  if (totalPages == null && totalItems != null && requestedPageSize > 0) {
    totalPages = Math.max(1, Math.ceil(totalItems / requestedPageSize));
  }
  if (totalPages == null) totalPages = 1;

  const isLastPage =
    mapped.length < requestedPageSize || (totalPages > 0 && resolvedPage >= totalPages);

  return {
    items: mapped,
    page: resolvedPage,
    pageSize: requestedPageSize,
    totalPages,
    isLastPage,
  };
}

export function mapSoxControlsListPage(body, { page, pageSize = 20 }) {
  const requestedPageSize = Math.max(1, Number(pageSize) || 20);
  const requestedPage = Math.max(1, Number(page) || 1);
  const root = body?.data && typeof body.data === 'object' ? body.data : body;

  const list = Array.isArray(root?.controls)
    ? root.controls
    : Array.isArray(root?.items)
      ? root.items
      : Array.isArray(root)
        ? root
        : [];

  const mapped = list
    .map((control) => {
      const value = String(control?.id ?? '').trim();
      if (!value) return null;
      const displayId = String(control?.control_id ?? control?.control_display_id ?? '').trim();
      const title = String(control?.title ?? '').trim();
      const labelLead = displayId || value;
      return {
        value,
        label: title ? `${labelLead} - ${title}` : labelLead,
      };
    })
    .filter(Boolean);

  const pag = root?.pagination ?? {};
  const resolvedPage = Number.isFinite(Number(pag.page)) ? Number(pag.page) : requestedPage;
  const totalItems = Number.isFinite(Number(pag.total_items)) ? Number(pag.total_items) : null;
  let totalPages = Number.isFinite(Number(pag.total_pages)) ? Math.max(1, Number(pag.total_pages)) : null;
  if (totalPages == null && totalItems != null && requestedPageSize > 0) {
    totalPages = Math.max(1, Math.ceil(totalItems / requestedPageSize));
  }
  if (totalPages == null) totalPages = 1;

  const isLastPage =
    mapped.length < requestedPageSize || (totalPages > 0 && resolvedPage >= totalPages);

  return {
    items: mapped,
    page: resolvedPage,
    pageSize: requestedPageSize,
    totalPages,
    isLastPage,
  };
}

export async function fetchRiskControlFilterOptionsPage({ page, pageSize = 20, search = '', classification = 'business' }) {
  const body = await getSoxControlsListPage({ page, pageSize, search, classification });
  return mapSoxControlsListPage(body, { page, pageSize });
}

const toNumber = (value) => (typeof value === 'number' && Number.isFinite(value) ? value : 0);

const normalizeRisksByCycle = (value) => {
  if (!Array.isArray(value)) return [];
  return value
    .map((row) => ({
      cycleId: String(row?.cycle_id ?? row?.cycleId ?? '').trim(),
      cycleName: String(row?.cycle_name ?? row?.cycleName ?? '').trim(),
      riskCount: toNumber(row?.risk_count ?? row?.riskCount),
    }))
    .filter((row) => row.cycleId || row.cycleName);
};

export async function fetchRisksDashboardMetrics() {
  const response = await axiosInstance.get('/api/risks/dashboard');
  const metrics = response?.data?.data ?? response?.data ?? {};

  return {
    total: toNumber(metrics?.total_count),
    unmapped: toNumber(metrics?.control_unmapped_count),
    processUnmapped: toNumber(metrics?.process_unmapped_count),
    risksByCycle: normalizeRisksByCycle(metrics?.risks_by_cycle),
  };
}

export async function appendRiskProcessMappings({ riskIds, processIds, auditYearId }) {
  return axiosInstance.post('/api/risks/process/append_mapping', {
    risk_ids: riskIds,
    process_ids: processIds,
    ...(auditYearId ? { audit_year_id: auditYearId } : {}),
  });
}

export async function appendRiskControlMappings({ riskIds, controlIds, auditYearId }) {
  return axiosInstance.post('/api/risks/control/append_mapping', {
    risk_ids: riskIds,
    control_ids: controlIds,
    ...(auditYearId ? { audit_year_id: auditYearId } : {}),
  });
}

const toTrimmedString = (value) => (value == null ? '' : String(value).trim());

const normalizeStringIdList = (value) => {
  if (!Array.isArray(value)) return [];
  return uniq(
    compact(
      value.map((item) => {
        if (item == null) return '';
        if (typeof item === 'object') {
          return toTrimmedString(item.id ?? item.node_id ?? item.nodeId ?? item.uuid);
        }
        return toTrimmedString(item);
      })
    )
  );
};

const pickAssociationProcessId = (row) =>
  toTrimmedString(
    row?.id ?? row?.process_uuid ?? row?.processUuid ?? row?.process_id ?? row?.processId
  );

const pickFromRow = (row, keys) => toTrimmedString(keys.map((key) => row?.[key]).find((v) => v != null && v !== ''));

/** Control UUID for API + form (`process_mappings.control_ids`). */
const controlUuidFromRow = (c) =>
  toTrimmedString(
    c?.id ?? c?.control_uuid ?? c?.controlUuid ?? c?.uuid ?? c?.control_id ?? c?.controlId
  );

/**
 * Maps GET payloads where each row is a process with optional nested `controls[]`,
 * or a flat `{ process_id, control_ids }` mapping row.
 */
function extractAssociationsFromProcessLikeRows(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return null;
  const out = rows
    .map((p) => {
      const nested = Array.isArray(p?.controls)
        ? p.controls
        : Array.isArray(p?.mitigating_controls)
          ? p.mitigating_controls
          : null;

      const processId = toTrimmedString(
        p?.id ?? p?.process_uuid ?? p?.processUuid ?? p?.process_id ?? p?.processId
      );
      if (!processId) return null;

      let mitigatingControlIds = [];
      if (nested && nested.length > 0) {
        mitigatingControlIds = nested.map((c) => controlUuidFromRow(c)).filter(Boolean);
      } else {
        mitigatingControlIds = normalizeStringIdList(p?.control_ids ?? p?.controlIds);
      }

      return { processId, linkedNodeIds: [], mitigatingControlIds };
    })
    .filter(Boolean);

  return out.length > 0 ? out : null;
}

const collectLinkedIds = ({ risk, rowLists, rowKeys, scalarKeys, listKeys }) => {
  const fromRows = rowLists
    .map((key) => risk[key])
    .filter(Array.isArray)
    .flatMap((rows) => rows.map((row) => pickFromRow(row, rowKeys)).filter(Boolean));

  const fromScalars = compact(scalarKeys.map((key) => toTrimmedString(risk[key])));

  const fromLists = compact(
    listKeys
      .map((key) => risk[key])
      .filter(Array.isArray)
      .flatMap((list) => compact(list.map((item) => toTrimmedString(item))))
  );

  return uniq(compact([...fromRows, ...fromScalars, ...fromLists]));
};

export function extractLinkedProcessIds(risk) {
  return collectLinkedIds({
    risk,
    rowLists: ['processes', 'process_links', 'processLinks', 'linked_processes', 'linkedProcesses'],
    rowKeys: ['id', 'process_uuid', 'processUuid', 'processId', 'process_id', 'uuid'],
    scalarKeys: ['process_id', 'processId', 'process_uuid', 'processUuid'],
    listKeys: ['process_ids', 'processIds'],
  });
}

export function extractLinkedControlIds(risk) {
  return collectLinkedIds({
    risk,
    rowLists: ['controls', 'control_links', 'controlLinks', 'linked_controls', 'linkedControls'],
    /** Prefer stable control UUID for payloads and lists. */
    rowKeys: [
      'id',
      'uuid',
      'control_uuid',
      'controlUuid',
      'control_id',
      'controlId',
      'control_display_id',
      'controlDisplayId',
      'sox_control_id',
      'soxControlId',
    ],
    scalarKeys: ['control_id', 'controlId', 'sox_control_id', 'soxControlId'],
    listKeys: ['control_ids', 'controlIds', 'sox_control_ids', 'soxControlIds'],
  });
}

/**
 * Build nested process → mitigating-control associations for the risk form.
 * Prefers `process_mappings` / `processMappings` (nested `controls[]` or flat `control_ids`);
 * then `process_associations` (legacy); then `processes[]` (same nested shape); then flat ids.
 * `linkedNodeIds` are always empty (not used in the UI).
 */
export function extractProcessAssociations(risk) {
  const rawMappings = risk?.process_mappings ?? risk?.processMappings;
  const fromMappings = extractAssociationsFromProcessLikeRows(rawMappings);
  if (fromMappings) return fromMappings;

  const rawAssoc = risk?.process_associations ?? risk?.processAssociations;
  if (Array.isArray(rawAssoc) && rawAssoc.length > 0) {
    return rawAssoc
      .map((row) => {
        const processId = pickAssociationProcessId(row);
        if (!processId) return null;
        return {
          processId,
          linkedNodeIds: [],
          mitigatingControlIds: normalizeStringIdList(
            row.mitigating_control_ids ?? row.mitigatingControlIds
          ),
        };
      })
      .filter(Boolean);
  }

  const processes = Array.isArray(risk?.processes) ? risk.processes : [];
  const flatControls = extractLinkedControlIds(risk);

  const fromProcesses = extractAssociationsFromProcessLikeRows(processes);
  if (fromProcesses) {
    if (flatControls.length === 0) return fromProcesses;
    return fromProcesses.map((entry) =>
      entry.mitigatingControlIds.length === 0
        ? { ...entry, mitigatingControlIds: [...flatControls] }
        : entry
    );
  }

  const processIds = extractLinkedProcessIds(risk);
  if (processIds.length === 0) return [];
  const controlIds = extractLinkedControlIds(risk);
  return processIds.map((processId) => ({
    processId,
    linkedNodeIds: [],
    mitigatingControlIds: controlIds.length ? [...controlIds] : [],
  }));
}

/**
 * Build control picker labels from the loaded risk payload (UUID value → display label).
 * Walks top-level `controls`, nested `processes[].controls`, and `process_mappings[].controls`.
 */
export function buildControlLabelOptionsFromRisk(risk) {
  if (!risk || typeof risk !== 'object') return [];
  const acc = [];
  const seen = new Set();

  const pushCtrl = (c) => {
    if (!c || typeof c !== 'object') return;
    const uuid = controlUuidFromRow(c);
    if (!uuid || seen.has(uuid)) return;
    seen.add(uuid);
    const displayId = toTrimmedString(
      c.control_display_id ?? c.controlDisplayId ?? c.control_id ?? c.controlId ?? c.sox_control_id
    );
    const title = toTrimmedString(c.control_title ?? c.title ?? c.name);
    const labelLead = displayId || uuid;
    acc.push({
      value: uuid,
      label: title ? `${labelLead} - ${title}` : labelLead,
    });
  };

  const top = risk.controls;
  if (Array.isArray(top)) top.forEach(pushCtrl);

  const processes = risk.processes;
  if (Array.isArray(processes)) {
    processes.forEach((proc) => {
      const nested = proc?.controls ?? proc?.mitigating_controls;
      if (Array.isArray(nested)) nested.forEach(pushCtrl);
    });
  }

  const mappingRows = [
    ...(Array.isArray(risk.process_mappings) ? risk.process_mappings : []),
    ...(Array.isArray(risk.processMappings) ? risk.processMappings : []),
  ];
  mappingRows.forEach((row) => {
    const nested = row?.controls ?? row?.mitigating_controls;
    if (Array.isArray(nested)) nested.forEach(pushCtrl);
  });

  return acc;
}

export function normalizeFormForPatch(values, schema) {
  return schema.cast(values, { stripUnknown: true });
}

export function areArraysEqual(left = [], right = []) {
  return isEqual(left, right);
}

export function buildChangedPatch(previous, current) {
  return buildPatchByKeys(previous, current, {
    scalarKeys: ['clientRiskId', 'riskDescription', 'riskCategory', 'riskLevel', 'itgcDomainId'],
    arrayKeys: ['processAssociations']
  });
}

/** Normalize risk fields for sort/filter (camelCase + snake_case from API). */
export function riskLevelOf(risk) {
  return risk?.riskLevel ?? risk?.risk_level ?? '';
}

/** Display string for a single row field (this page uses snake_case keys from the API). */
export function strRiskField(risk, key) {
  const value = risk?.[key];
  return value == null || value === '' ? '' : String(value);
}
